import { BadgeList } from '@components/shared/badge-list.component';
import { Tile } from '@components/shared/tile.component';
import { useGetProducts } from '@queries/product.query';
import { TechnologyHeaderTab } from '@utils/query-state.util';
import { pluralize } from '@utils/text.util';
import { ArrowRightIcon, PackageIcon } from 'lucide-react';
import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';

type ProductsCompactProps = {
  onNavigateToDetails?: (tab: TechnologyHeaderTab) => void;
};

export const ProductsCompact: FC<ProductsCompactProps> = (props) => {
  const { onNavigateToDetails } = props;
  const { technologyId } = useParams();
  const { data: products = [], isPending: isProductsLoading } = useGetProducts(technologyId);

  const sortedProducts = useMemo(() => {
    const filtered = products.filter((product) =>
      technologyId ? product.technologies?.some((tech) => tech.id === technologyId) : false,
    );
    return filtered.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [products, technologyId]);

  const productsCount = sortedProducts.length;
  const canNavigate = Boolean(onNavigateToDetails);

  const handleNavigate = () => {
    onNavigateToDetails?.('details');
  };

  // Don't show if no products
  if (!isProductsLoading && productsCount === 0) {
    return null;
  }

  return (
    <Tile
      className="min-h-fit"
      header={{
        iconButton: canNavigate
          ? {
              icon: <ArrowRightIcon size={16} />,
              onClick: handleNavigate,
            }
          : undefined,
        title: 'Related Products',
      }}
      hoverReveal={canNavigate}
      isLoading={isProductsLoading}
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <PackageIcon
            className="text-gray-400"
            size={16}
          />
          <span className="text-sm font-medium text-gray-900">{pluralize(productsCount, 'Product')}</span>
        </div>
        {productsCount > 0 && (
          <div className="pl-6">
            <BadgeList
              items={sortedProducts}
              limit={6}
              size="xs"
            />
          </div>
        )}
      </div>
    </Tile>
  );
};
